import { Component, inject, input, NgZone, output } from '@angular/core';
import { Router } from '@angular/router';
import { VisibilitySettingsNotificationDialogComponent } from '@app/features/visibility-settings-notification-dialog/visibility-settings-notification-dialog.component';
import { DialogConfig, DialogService } from '@app/core/services/dialog.service';
import { NotificationApi } from '@app/core/services/api/notification.api';
import { SearchService } from '@app/core/services/search.service';
import { SkillsStore } from '@app/pages/home/tree/store/skills.store';
import { UUID } from '@app/_api/identifyable';
import {
  getNotificationEditButtonLabel,
  getNotificationMessage,
  getNotificationTitle,
  NotificationAction,
  NotificationDto,
} from '@app/shared/notification-center/notifications.types';
import { ConfirmationDialogComponent, ConfirmationDialogModel } from '@app/shared/confirmation-dialog/confirmation-dialog.component';
import { CertificateStore } from '@app/pages/certificate/store/certificate.store';

@Component({
  selector: 'if-notification-card',
  imports: [],
  templateUrl: './notification-card.component.html',
})
export class NotificationCardComponent {
  readonly notification = input.required<NotificationDto>();
  readonly deleteEvent = output<void>();

  private readonly zone = inject(NgZone);
  private readonly dialogService = inject(DialogService);
  private readonly notificationApi = inject(NotificationApi);
  private readonly searchService = inject(SearchService);
  private readonly skillsStore = inject(SkillsStore);
  private readonly certificateStore = inject(CertificateStore);
  private readonly router = inject(Router);

  protected getSkillName = (skillId: UUID) => {
    return this.skillsStore.entityMap()[skillId]?.title ?? 'Skill Titel nicht gefunden';
  };

  protected getCertificateName = (certificateId: UUID) => {
    return this.certificateStore.entityMap()[certificateId]?.title ?? 'Zertifikat Titel nicht gefunden';
  };

  protected getNotificationTitle = (action: NotificationAction) => getNotificationTitle(action);
  protected getNotificationMessage = (action: NotificationAction) =>
    getNotificationMessage(action, this.getSkillName, this.getCertificateName);
  protected getNotificationEditButtonLabel = (action: NotificationAction) => getNotificationEditButtonLabel(action);

  protected delete() {
    this.notificationApi.deleteNotification(this.notification().id).subscribe({
      next: () => this.deleteEvent.emit(),
      // eslint-disable-next-line no-console
      error: () => console.debug('Notification could not be deleted'),
    });
  }

  protected handleAction(): void {
    const action = this.notification().action;
    switch (action.type) {
      case 'CHECK_VISIBILITY':
        this.dialogService.open(VisibilitySettingsNotificationDialogComponent);
        break;
      case 'SKILL_MERGED':
      case 'SKILL_MOVED':
        this.focusSkill(action.skillId);
        break;
      case 'SUGGESTION_CREATED':
        this.router.navigate(['/admin-settings'], {
          queryParams: {
            tab: 'suggestions',
          },
        });
        break;
      default:
    }
  }

  private focusSkill(skillId: UUID) {
    const skill = this.skillsStore.entityMap()[skillId];
    if (!skill) {
      this.showErrorDialog();
      return;
    }
    const skillTitle = skill.title;
    this.searchService.$currentSearch.set(skillTitle);
  }

  private showErrorDialog() {
    const action = this.notification().action;
    let skillTitle = 'Skill Titel konnte nicht gefunden werden';
    if (action.type === 'SKILL_MOVED') {
      skillTitle = action.movedTitle;
    }
    if (action.type === 'SKILL_MERGED') {
      skillTitle = action.sourceTitle;
    }
    const title = 'Skill nicht gefunden';
    const message = `Der Skill ${skillTitle} konnte nicht gefunden werden, vermutlich wurde er inzwischen gelöscht. Du kannst die Benachrichtigung nun löschen.`;
    this.zone.run(() => {
      const dialogConfig: DialogConfig = {
        data: {
          title,
          message,
          allowCancel: false,
          confirmText: 'Schließen',
        } satisfies ConfirmationDialogModel,
      } satisfies DialogConfig;
      this.dialogService.open(ConfirmationDialogComponent, dialogConfig);
    });
  }
}
